import { Mark, Node, Extension } from 'tiptap';
import { HardBreak } from 'tiptap-extensions';
import { removeMark, setBlockType, toggleBlockType, updateMark } from 'tiptap-commands';
import { getMarkAttrs } from 'tiptap-utils';
import { composeBlockStyle, isPlaceholderBreak } from './emailHtmlSerialization';

const textAlign = value => (value ? String(value) : null);
const blockTextAlign = dom => {
  const inline = textAlign(dom.style.textAlign);
  if (inline) return inline;
  const match = String(dom.className || '').match(/(?:^|\s)ql-align-(center|right|justify)(?:\s|$)/);
  return match ? match[1] : null;
};

export class AlignableParagraph extends Node {
  get name() {
    return 'paragraph';
  }

  get defaultOptions() {
    return { noMargin: false };
  }

  get schema() {
    return {
      content: 'inline*',
      group: 'block',
      attrs: {
        textAlign: { default: null },
        // Keep legacy Quill's block-level inline styles when converting into
        // TipTap so colors, sizes, and email-specific spacing survive.
        style: { default: null }
      },
      parseDOM: [
        {
          tag: 'p',
          getAttrs: dom => ({
            textAlign: blockTextAlign(dom),
            style: dom.getAttribute('style') || null
          })
        }
      ],
      toDOM: node => [
        'p',
        { style: composeBlockStyle(node, this.options.noMargin) },
        0
      ]
    };
  }

  commands({ type }) {
    return attrs => setBlockType(type, attrs || {});
  }
}

export class AlignableHeading extends Node {
  get name() {
    return 'heading';
  }

  get defaultOptions() {
    return { levels: [1, 2, 3, 4, 5, 6], noMargin: false };
  }

  get schema() {
    return {
      attrs: {
        level: { default: 1 },
        textAlign: { default: null },
        style: { default: null }
      },
      content: 'inline*',
      group: 'block',
      defining: true,
      parseDOM: this.options.levels.map(level => ({
        tag: `h${level}`,
        getAttrs: dom => ({
          level,
          textAlign: blockTextAlign(dom),
          style: dom.getAttribute('style') || null
        })
      })),
      toDOM: node => [
        `h${node.attrs.level}`,
        {
          style: [
            node.attrs.style,
            this.options.noMargin && 'margin: 0; padding-left: 5px; padding-right: 5px',
            node.attrs.textAlign && `text-align: ${node.attrs.textAlign}`
          ].filter(Boolean).join('; ')
        },
        0
      ]
    };
  }

  commands({ type, schema }) {
    return {
      toggleHeading: attrs => toggleBlockType(type, schema.nodes.paragraph, attrs || {})
    };
  }
}

// A paragraph whose only child is <br> is the saved form of an empty
// paragraph. Ignoring that break keeps it empty in the document, so the
// editor shows one line and the next save does not add another <br>.
export class EmailHardBreak extends HardBreak {
  get schema() {
    return {
      inline: true,
      group: 'inline',
      selectable: false,
      parseDOM: [
        {
          tag: 'br',
          priority: 60,
          ignore: true,
          getAttrs: dom => (isPlaceholderBreak(dom) ? null : false)
        },
        { tag: 'br' }
      ],
      toDOM: () => ['br']
    };
  }
}

export class StyledText extends Mark {
  get name() {
    return 'styled_text';
  }

  get schema() {
    return {
      attrs: {
        color: { default: null },
        backgroundColor: { default: null },
        fontSize: { default: null },
        fontFamily: { default: null }
      },
      parseDOM: [
        {
          tag: 'span[style]',
          getAttrs: dom => ({
            color: dom.style.color || null,
            backgroundColor: dom.style.backgroundColor || null,
            fontSize: dom.style.fontSize || null,
            fontFamily: dom.style.fontFamily || null
          })
        }
      ],
      toDOM: node => {
        const style = [
          node.attrs.color && `color: ${node.attrs.color}`,
          node.attrs.backgroundColor && `background-color: ${node.attrs.backgroundColor}`,
          node.attrs.fontSize && `font-size: ${node.attrs.fontSize}`,
          node.attrs.fontFamily && `font-family: ${node.attrs.fontFamily}`
        ].filter(Boolean).join('; ');
        return ['span', style ? { style: `${style};` } : {}, 0];
      }
    };
  }

  commands({ type }) {
    return {
      setInlineStyle: attrs => (state, dispatch) => {
        const current = getMarkAttrs(state, type) || {};
        const next = { ...current, ...attrs };
        if (Object.values(next).every(value => !value)) {
          return removeMark(type)(state, dispatch);
        }
        return updateMark(type, next)(state, dispatch);
      }
    };
  }
}

export class TextAlign extends Extension {
  get name() {
    return 'text_align';
  }

  commands() {
    return {
      setTextAlign: attrs => (state, dispatch) => {
        const requested = attrs && attrs.textAlign ? String(attrs.textAlign) : null;
        const { from, to, $from, $to } = state.selection;
        const updates = [];
        const add = (node, pos) => {
          if ((node.type.name === 'paragraph' || node.type.name === 'heading') && !updates.some(item => item.pos === pos)) {
            updates.push({ node, pos });
          }
        };

        if ($from.sameParent($to)) {
          add($from.parent, $from.before($from.depth));
        } else {
          state.doc.nodesBetween(from, to, (node, pos) => add(node, pos));
        }

        if (dispatch) {
          const transaction = state.tr;
          updates.forEach(({ node, pos }) => {
            transaction.setNodeMarkup(pos, node.type, {
              ...node.attrs,
              textAlign: requested
            });
          });
          dispatch(transaction);
        }
        return updates.length > 0;
      }
    };
  }
}

export const legacyHtmlNeedsSourceMode = html =>
  /<(table|style|html|head|body|tbody|thead|tfoot|tr|td|th)\b/i.test(String(html || ''));

export { htmlIsEmpty } from '../../components/emailContentValidation';
